import {
  ButtonVariants,
  EMPTY_ARRAY,
  formattedUTCDay,
  FormControlSizes,
  getAgGridColId,
  IconName,
  useDefaultColumns,
  type CareOrder,
  type Column,
  type ColumnDef,
  type Leaves,
} from '@talos/kyoko';
import type { GridApi, IRowNode } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../../hooks';
import { getRowTypeColumn } from '../columns/rowTypeColumn';

interface UseCareOrderNettingPreviewColumnsParams {
  defaultColumns?: (keyof CareOrder | Partial<Column>)[];

  actionColumn?: {
    type: 'add' | 'remove';
    onClick: (careOrder: CareOrder) => void;
  };
}

export const useCareOrderNettingPreviewColumns = ({
  defaultColumns = EMPTY_ARRAY,
  actionColumn,
}: UseCareOrderNettingPreviewColumnsParams) => {
  const { enableCareOrderExternalIDField, enableCareOrderSettleDateField, supportAdjustedSettlementDate } =
    useFeatureFlag();

  const actionColumnDefinition = useCareOrderNettingActionColumn(actionColumn);

  const columnDefinitions = useMemo(
    () =>
      new Map(
        compact([
          {
            type: 'id',
            field: 'OrderID',
            title: 'ID',
            initialWidth: 90,
          },
          {
            type: 'id',
            field: 'nettedOrderID',
            title: 'Netted Order ID',
            initialHide: true,
            initialWidth: 120,
          },
          getRowTypeColumn<CareOrder>({
            getRowType: data => (data.isNetted ? 'Netted' : 'Care Order'),
          }),
          {
            type: 'date',
            field: 'SubmitTime',
            title: 'Submit Time',
            params: { milliseconds: true },
            initialWidth: 150,
            initialSort: '-',
          },
          {
            type: 'careOrderStatus',
            field: 'status',
            title: 'Status',
            initialWidth: 130,
          },
          {
            type: 'side',
            field: 'Side',
            title: 'Side',
          },
          {
            type: 'size',
            field: 'OrderQty',
            title: 'Order Qty',
            initialWidth: 100,
            params: {
              currencyField: 'Currency' satisfies Leaves<CareOrder>,
            },
          },
          {
            type: 'security',
            field: 'Symbol',
            title: 'Symbol',
            initialWidth: 160,
          },
          {
            type: 'size',
            field: 'WorkingQty',
            title: 'Working Qty',
            params: {
              currencyField: 'Currency' satisfies Leaves<CareOrder>,
            },
          },
          {
            type: 'counterparty',
            field: 'Counterparty',
            title: 'Counterparty',
            initialWidth: 180,
          },
          {
            type: 'text',
            field: 'Annotations.ETP.InitiatingFrom',
            title: 'Initiating From',
            initialWidth: 140,
          },
          { field: 'User', type: 'user' },

          { field: 'TransactTime', type: 'date' },
          {
            type: 'size',
            field: 'AvailableQty',
            title: 'Remaining Qty',
            params: {
              currencyField: 'Currency' satisfies Leaves<CareOrder>,
            },
          },
          {
            type: 'size',
            field: 'CumQty',
            title: 'Filled Qty',
            params: {
              currencyField: 'Currency' satisfies Leaves<CareOrder>,
            },
          },
          {
            field: 'CumAmt',
            title: 'Cum Amt',
            type: 'size',
            initialHide: true,
            params: { currencyField: 'AmountCurrency' satisfies Leaves<CareOrder> },
          },
          {
            type: 'number',
            field: 'units',
            title: 'Units',
            initialHide: true,
            initialWidth: 100,
          },
          {
            type: 'text',
            field: 'fund',
            initialHide: true,
          },
          {
            type: 'text',
            field: 'Group',
            title: 'Group',
            initialWidth: 120,
          },
          enableCareOrderExternalIDField && {
            type: 'text',
            field: 'ExternalGroup',
            title: 'External ID',
          },
          enableCareOrderSettleDateField && {
            type: 'date',
            field: 'SettleDate',
            title: 'Settlement Date',
            params: { customFormatter: formattedUTCDay },
          },
          enableCareOrderSettleDateField &&
            supportAdjustedSettlementDate && {
              type: 'custom',
              field: 'Annotations.SettleOffset',
              title: 'Settle Offset',
              params: { valueFormatter: ({ value }) => (value ? `T+${value}` : '') },
              initialWidth: 100,
            },
          { field: 'Text', type: 'text' },
          {
            type: 'text',
            field: 'Annotations.FIX.OrderOriginationTrader',
            title: 'Origination Trader',
            initialHide: true,
          },
          {
            field: 'approvedMarkets',
            type: 'markets',
            title: 'Approved Markets',
            params: { emptyArrayText: 'All available' },
            initialHide: true,
          },
          {
            type: 'price',
            field: 'AvgPx',
            title: 'Filled Price (no fees)',
            initialHide: true,
            params: {
              assetField: 'Symbol' satisfies Leaves<CareOrder>,
            },
          },
          {
            type: 'text',
            field: 'Comments',
            title: 'Comments',
            initialHide: true,
          },
          {
            field: 'subAccounts',
            type: 'subAccount',
            title: 'Sub Account(s)',
          },
          {
            id: 'care-order-management-product-type',
            type: 'productType',
            initialHide: true,
            title: 'Product',
            initialWidth: 100,
          },
          {
            field: 'Timestamp',
            type: 'date',
            title: 'Updated at',
            params: { milliseconds: true },
          },
          { field: 'ClOrdID', type: 'id', initialHide: true, initialWidth: 80 },
          {
            field: 'LeavesQty',
            type: 'size',
            initialHide: true,
            params: { currencyField: 'Currency' satisfies Leaves<CareOrder> },
            title: 'Open Qty',
          },
          {
            field: 'Currency',
            type: 'currency',
            initialHide: true,
          },

          {
            field: 'AmountCurrency',
            type: 'currency',
            initialHide: true,
          },
          actionColumnDefinition ?? false,
        ] satisfies (ColumnDef<CareOrder> | false)[]).map(column => [getAgGridColId(column), column])
      ),
    [
      actionColumnDefinition,
      enableCareOrderExternalIDField,
      enableCareOrderSettleDateField,
      supportAdjustedSettlementDate,
    ]
  );

  return useDefaultColumns(defaultColumns, columnDefinitions);
};

const useCareOrderNettingActionColumn = (
  actionColumn: UseCareOrderNettingPreviewColumnsParams['actionColumn']
): ColumnDef<CareOrder> | undefined =>
  useMemo(() => {
    if (actionColumn == null) {
      return undefined;
    }

    const onClick = ({ node }: { node: IRowNode<CareOrder>; api: GridApi<CareOrder> }) => {
      const careOrder = node.data;
      if (careOrder == null) {
        return;
      }

      actionColumn.onClick(careOrder);
    };

    if (actionColumn.type === 'add') {
      return {
        type: 'button',
        id: 'care-order-netting-add-action',
        title: '',
        pinned: 'right',
        frozen: true,
        width: 70,
        minWidth: 70,
        maxWidth: 70,
        suppressColumnsToolPanel: true,
        exportable: false,
        params: {
          size: FormControlSizes.Small,
          startIcon: IconName.Plus,
          variant: ButtonVariants.Primary,
          children: 'Add',
          onClick,
        },
      } satisfies ColumnDef<CareOrder>;
    }

    return {
      type: 'button',
      id: 'care-order-netting-remove-action',
      title: '',
      pinned: 'right',
      frozen: true,
      width: 38,
      minWidth: 38,
      maxWidth: 38,
      suppressColumnsToolPanel: true,
      exportable: false,
      params: {
        size: FormControlSizes.Small,
        startIcon: IconName.Close,
        onClick,
      },
    } satisfies ColumnDef<CareOrder>;
  }, [actionColumn]);
